import { useState } from "react";
import { api, formatApiErrorDetail } from "../lib/apiClient";
import { EVENT } from "../config";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import { Check, Plus, X } from "lucide-react";

const emptyGuest = () => ({ first_name: "", last_name: "", attending: "yes", allergies: "" });

const inputClass =
  "h-12 border-[#DCD3C0] bg-[#F2EDE3] text-[#3F3A32] placeholder:text-[#AFA58F] focus-visible:border-[#7A6A5E] focus-visible:ring-0";

export const RsvpForm = () => {
  const [guests, setGuests] = useState([emptyGuest()]);
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [website, setWebsite] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [doneName, setDoneName] = useState("");
  const [doneAttending, setDoneAttending] = useState(true);

  const updateGuest = (i, field, value) =>
    setGuests((gs) => gs.map((g, j) => (j === i ? { ...g, [field]: value } : g)));

  const addCompanion = () => setGuests((gs) => [...gs, emptyGuest()]);
  const removeGuest = (i) => setGuests((gs) => gs.filter((_, j) => j !== i));

  const reset = () => {
    setGuests([emptyGuest()]);
    setEmail("");
    setPhone("");
    setMessage("");
    setDone(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!guests[0].first_name.trim() || !guests[0].last_name.trim()) {
      toast.error("Please enter a first and last name for at least one guest.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        email: email.trim(),
        phone: phone.trim(),
        message: message.trim(),
        website,
        guests: guests
          .filter((g) => g.first_name.trim() || g.last_name.trim())
          .map((g) => ({
            first_name: g.first_name.trim(),
            last_name: g.last_name.trim(),
            attending: g.attending === "yes",
            allergies: g.allergies.trim(),
          })),
      };
      await api.post("/rsvp", payload);
      setDoneName(guests[0].first_name.trim());
      setDoneAttending(payload.guests.some((g) => g.attending));
      setDone(true);
      toast.success("Thank you for your reply!");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail, "Something went wrong. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <section id="osa" className="px-6 py-16 md:px-16 md:py-24" data-testid="rsvp-section">
        <div className="mx-auto max-w-2xl border border-[#C9BFA9] bg-[#FAF7F0] p-12 text-center" data-testid="rsvp-success">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#3F3A32]">
            <Check className="h-8 w-8 text-[#F5F1E8]" strokeWidth={1.5} />
          </div>
          <h2 className="mt-8 font-display text-3xl font-light tracking-tight text-[#3F3A32] md:text-4xl">
            Your RSVP has been received
          </h2>
          <p className="mt-4 font-light leading-relaxed text-[#5E574B]">
            {doneAttending
              ? `Thank you, ${doneName}! Your reply is safely registered – we can't wait to celebrate with you on Sandhamn, ${EVENT.dateLabel}.`
              : `Thank you, ${doneName}! Your reply is safely registered. We're sorry you can't make it – you will be missed.`}
          </p>
          <button
            onClick={reset}
            data-testid="rsvp-reset-button"
            className="mt-10 border border-[#DCD3C0] px-8 py-3 text-sm uppercase tracking-[0.15em] text-[#7A7264] transition-colors hover:border-[#7A6A5E] hover:text-[#3F3A32]"
          >
            Send another reply
          </button>
        </div>
      </section>
    );
  }

  return (
    <section id="osa" className="px-6 py-16 md:px-16 md:py-24" data-testid="rsvp-section">
      <div className="mx-auto max-w-2xl">
        <p className="mb-3 text-xs uppercase tracking-[0.3em] text-[#7A6A5E]">RSVP</p>
        <h2 className="font-display text-3xl font-light tracking-tight text-[#3F3A32] md:text-5xl">
          I can't wait to celebrate with you
        </h2>
        <p className="mt-4 font-light text-[#7A7264]">
          Thank you for taking the time to RSVP. I'm truly looking forward to celebrating
          this unforgettable weekend with all of you in Sandhamn.
        </p>
        <p className="mt-2 text-sm font-light text-[#948B7B]" data-testid="rsvp-deadline">
          Kindly reply by the 15th of September.
        </p>

        <form onSubmit={submit} className="mt-12 space-y-8" data-testid="rsvp-form">
          <input
            type="text"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            style={{ position: "absolute", left: "-9999px", height: 0, width: 0, opacity: 0 }}
            data-testid="rsvp-honeypot"
          />

          {guests.map((g, i) => (
            <div key={i} className="space-y-5 border border-[#DCD3C0] bg-[#FAF7F0] p-6" data-testid={`rsvp-guest-${i}`}>
              <div className="flex items-center justify-between">
                <p className="text-xs uppercase tracking-[0.2em] text-[#7A6A5E]">
                  {i === 0 ? "Guest" : `Companion ${i}`}
                </p>
                {i > 0 && (
                  <button
                    type="button"
                    onClick={() => removeGuest(i)}
                    data-testid={`rsvp-remove-guest-${i}`}
                    className="inline-flex items-center gap-1 text-xs uppercase tracking-wider text-[#948B7B] transition-colors hover:text-[#3F3A32]"
                  >
                    <X className="h-3.5 w-3.5" /> Remove
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.15em] text-[#7A7264]">First name</Label>
                  <Input
                    value={g.first_name}
                    onChange={(e) => updateGuest(i, "first_name", e.target.value)}
                    required={i === 0}
                    data-testid={`rsvp-first-name-${i}`}
                    className={inputClass}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.15em] text-[#7A7264]">Last name</Label>
                  <Input
                    value={g.last_name}
                    onChange={(e) => updateGuest(i, "last_name", e.target.value)}
                    required={i === 0}
                    data-testid={`rsvp-last-name-${i}`}
                    className={inputClass}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.15em] text-[#7A7264]">Will you attend?</Label>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { v: "yes", l: "Yes, I'll be there" },
                    { v: "no", l: "Unfortunately not" },
                  ].map((opt) => (
                    <button
                      key={opt.v}
                      type="button"
                      onClick={() => updateGuest(i, "attending", opt.v)}
                      data-testid={`rsvp-attending-${opt.v}-${i}`}
                      className={`border px-4 py-3 text-sm transition-colors ${
                        g.attending === opt.v
                          ? "border-[#3F3A32] bg-[#3F3A32] text-[#F5F1E8]"
                          : "border-[#DCD3C0] text-[#7A7264] hover:border-[#C4B99F]"
                      }`}
                    >
                      {opt.l}
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.15em] text-[#7A7264]">
                  Dietary preferences or allergies
                </Label>
                <Input
                  value={g.allergies}
                  onChange={(e) => updateGuest(i, "allergies", e.target.value)}
                  placeholder="E.g. gluten-free, nut allergy, vegetarian…"
                  data-testid={`rsvp-allergies-${i}`}
                  className={inputClass}
                />
              </div>
            </div>
          ))}

          <button
            type="button"
            onClick={addCompanion}
            data-testid="rsvp-add-companion"
            className="inline-flex items-center gap-2 border border-[#DCD3C0] px-6 py-3 text-sm uppercase tracking-[0.15em] text-[#7A6A5E] transition-colors hover:border-[#7A6A5E] hover:text-[#3F3A32]"
          >
            <Plus className="h-4 w-4" /> Add companion
          </button>

          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.15em] text-[#7A7264]">Email</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="rsvp-email"
                className={inputClass}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.15em] text-[#7A7264]">Phone number (optional)</Label>
              <Input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                data-testid="rsvp-phone"
                className={inputClass}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.15em] text-[#7A7264]">
              Message to the host (optional)
            </Label>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              data-testid="rsvp-message"
              className="min-h-[90px] border-[#DCD3C0] bg-[#F2EDE3] text-[#3F3A32] placeholder:text-[#AFA58F] focus-visible:border-[#7A6A5E] focus-visible:ring-0"
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            data-testid="rsvp-submit-button"
            className="w-full bg-[#3F3A32] py-5 text-sm font-medium uppercase tracking-[0.2em] text-[#F5F1E8] transition-colors hover:bg-[#57503F] disabled:opacity-50"
          >
            {submitting ? "Sending…" : "Send reply"}
          </button>
        </form>
      </div>
    </section>
  );
};
